import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, List, Row, Tag } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import { toDependentFileTypeColor } from "../../constants";
import { SubmitStatus } from "../../api/work-order-api";
import ApiContext from "../../api/context";
import { useContext, useState } from "react";
import Dragger from "antd/es/upload/Dragger";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { Template } from "../../api/models";

type TemplateCreateProps = {
  onClose: () => void,
  onCreate: (template: Template) => void
};

type DependentFiles = {
  [filePath: string]: boolean
};

export default function (p: TemplateCreateProps) {
  const [templateName, setTemplateName] = useState("");
  const [cavalryJson, setCavalryJson] = useState<Object>();
  const [dependentFiles, setDependentFiles] = useState<DependentFiles>({});
  const [validationSubmitStatus, setValidationSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [creationSubmitStatus, setCreationSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [error, setError] = useState<string>();
  const { api } = useContext(ApiContext);
  const isValidationSubmitting = validationSubmitStatus === SubmitStatus.Submitting;
  const isCreationSubmitting = creationSubmitStatus === SubmitStatus.Submitting;
  const canSave = !isCreationSubmitting &&
    templateName.trim() !== "" &&
    cavalryJson !== undefined &&
    !isValidationSubmitting;

  function emptyDependentFilesListText() {
    if (!cavalryJson) {
      return "Upload a Cavalry scene file to determine the dependent files."
    }

    return "There are no dependent files needed by this Cavalry scene.";
  }

  return (
    <>
      <Title style={titleStyle} level={4}>Create Template</Title>
      <Form
        layout="vertical"
        onFinish={_ => {
          setCreationSubmitStatus(SubmitStatus.Submitting);
          const template: Template = {
            name: templateName,
            cavalryProject: cavalryJson,
            deleted: false
          };

          api?.postTemplate(template).then(templateResponse => {
            const isGood = templateResponse.good();
            if (!isGood) {
              setError("There was a problem processing the request.");
              setCreationSubmitStatus(SubmitStatus.Idle);
              return;
            }

            p.onCreate(templateResponse.body!);
            setCreationSubmitStatus(SubmitStatus.Submitted);
          }).finally(() => {
            setCreationSubmitStatus(SubmitStatus.Idle);
          });
        }}
        colon={false}>
        <Form.Item label="Template Name">
          <Input
            placeholder="Enter a template name"
            disabled={isCreationSubmitting}
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
          />
        </Form.Item>
        <Form.Item label="Upload a Cavalry Scene">
          <Dragger
            accept=".cv"
            name="file"
            listType={undefined}
            multiple={false}
            action={undefined}
            showUploadList={false}
            fileList={[]}
            beforeUpload={() => false}
            onChange={e => {
              const isCavlaryFile = e.file.name.toLowerCase().endsWith(".cv");
              if (!isCavlaryFile) {
                setError("File provided must have the Cavalry scene (.cv) extension.");
                return;
              }

              if (!e.fileList[0].originFileObj) {
                setError("File could not be read.");
                return;
              }

              const reader = new FileReader();
              reader.onerror = _ => setError("Failed to parse the scene file.");
              reader.onload = e => {
                const json = JSON.parse(reader.result as string);
                setCavalryJson(json);
                setValidationSubmitStatus(SubmitStatus.Submitting);
                api?.postTemplateValidation(json).then(response => {
                  // Validate response.
                  const isGood = response.good();
                  if (!isGood) {
                    setError("There was a problem while trying to validate the scene.");
                    setCavalryJson(undefined);
                    return;
                  }

                  // Add file assets.
                  const validatedFiles: DependentFiles = {};
                  for (const file of response.body!.files ?? []) {
                    validatedFiles[file] = true;
                  }

                  // Add file sequence assets.
                  for (const fileSequence of response.body!.fileSequences ?? []) {
                    validatedFiles[fileSequence.assetPathPattern] = true;
                  }

                  // Add other assets.
                  for (const csvParameter of response.body!.csvParameters ?? []) {
                    validatedFiles[csvParameter.assetPath] = false;
                  }
                  for (const smartFolder of response.body!.smartFolderParameters ?? []) {
                    validatedFiles[smartFolder.assetPath] = false;
                  }

                  setDependentFiles(validatedFiles);
                }).finally(() => {
                  setValidationSubmitStatus(SubmitStatus.Idle);
                });
              };
              reader.readAsText(e.fileList[0].originFileObj);

              setError(undefined);
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag a file to this area to upload.</p>
          </Dragger>
        </Form.Item>
        <Form.Item label="Dependent Files">
          <List
            bordered={false}
            footer={false}
            pagination={{ pageSize: 5, hideOnSinglePage: Object.keys(dependentFiles).length < 6 }}
            size="small"
            dataSource={Object.keys(dependentFiles)}
            locale={{ emptyText: emptyDependentFilesListText() }}
            renderItem={(filePath: string) =>
              <List.Item key={filePath}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{filePath}</span>
                  <Tag color={toDependentFileTypeColor(dependentFiles[filePath])}>
                    {dependentFiles[filePath] ? "Template Asset" : "Work Order Asset"}
                  </Tag>
                </Row>
              </List.Item>
            }
          >
          </List>
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            disabled={isCreationSubmitting}
            onClick={p.onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            style={buttonStyle}
            loading={isCreationSubmitting}
            disabled={!canSave}>
            Create
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}

